<template>
    <page :busy="busy">
        <div>
            <grid
                ref="grid"
                :column-defs="columnDefs"
                :is-auto-height="true"
                :is-search="false"
                :row-data="packagesRows"
                :get-row-style="getRowStyle"
                :get-row-height="getRowHeight"
            >
                <responsive-button :icon="['fas', 'arrow-left']" variant="primary" @click="returnToBillingProfile"
                    >Return to Billing Profile</responsive-button
                >
            </grid>
        </div>
        <subscription-change-confirmation-modal
            title="Upgrade / Downgrade Account"
            :modal-id="subscriptionChangeConfirmationModalId"
            @success-change="handleChangeSubscriptionSuccess"
        />
    </page>
</template>

<script>
    import isNil from 'lodash/isNil';
    import page from '../../generic/page/page.vue';
    import API from '../../../api';
    import grid from '../../generic/grid/grid.vue';
    import isEmpty from 'lodash/isEmpty';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import SubscriptionChangeConfirmationModal from './subscription-change-confirmation-modal.vue';
    import {
        SUBSCRIPTION_CHANGE_DATA_ENDPOINT,
        SUBSCRIPTION_CHANGE_CANCEL_DOWNGRADE_ENDPOINT,
    } from '../../../endpoints';

    export default {
        components: {
            page,
            grid,
            ResponsiveButton,
            SubscriptionChangeConfirmationModal,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        mounted() {
            this.fetchChangeData();
        },
        data() {
            return {
                busy: false,
                subscription: null,
                packagesRows: [],
                subscriptionChangeConfirmationModalId: 'subscription-change-confirmation-modal',
                newPackageID: null,
            };
        },
        computed: {
            columnDefs() {
                return [
                    {
                        headerName: 'Package',
                        field: 'name',
                        minWidth: 120,
                        maxWidth: 120,
                        sortable: false,
                    },
                    {
                        headerName: 'Description',
                        field: 'description',
                        sortable: false,
                    },
                    {
                        headerName: 'Trial Period',
                        field: 'trial_period',
                        minWidth: 150,
                        maxWidth: 150,
                        sortable: false,
                    },
                    {
                        headerName: 'Max Properties',
                        field: 'max_properties',
                        minWidth: 130,
                        maxWidth: 130,
                        sortable: false,
                        valueParser: (data) => data.max_properties,
                    },
                    {
                        headerName: 'Billing Period',
                        field: 'billing_period',
                        minWidth: 120,
                        maxWidth: 120,
                        sortable: false,
                    },
                    {
                        headerName: 'Price',
                        field: 'amount',
                        minWidth: 120,
                        maxWidth: 120,
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        sortable: false,
                    },
                    {
                        headerName: 'Status',
                        field: 'package_status',
                        minWidth: 200,
                        maxWidth: 200,
                        sortable: false,
                    },
                    {
                        headerName: '',
                        minWidth: 170,
                        maxWidth: 170,
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onChange: async newPackageID => {
                                this.newPackageID = newPackageID;
                                this.$bvModal.show(this.subscriptionChangeConfirmationModalId);
                            },
                            onCancel: async packageID => {
                                this.cancelDowngrade(packageID);
                            },
                        },
                        sortable: false,
                    },
                ];
            },
        },
        methods: {
            async fetchChangeData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(SUBSCRIPTION_CHANGE_DATA_ENDPOINT());
                    this.subscription = data.subscription;
                    this.packagesRows = data.packages;
                } catch (e) {
                    this.errorToast({ text: 'Could not load subscription change data.' });
                } finally {
                    this.busy = false;
                }
            },
            async cancelDowngrade(packageID) {
                this.loading = true;
                try {
                    await API.put(SUBSCRIPTION_CHANGE_CANCEL_DOWNGRADE_ENDPOINT(packageID));

                    this.fetchChangeData();
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Something went wrong while cancelling subscription downgrade.',
                    });
                } finally {
                    this.loading = false;
                }
            },
            getRowStyle({ data, node }) {
                if (data.package_status === 'Current package') {
                    return { 'font-weight': 'bold' };
                }
            },
            getRowHeight() {
                return 45;
            },
            handleChangeSubscriptionSuccess() {
                this.fetchChangeData();
            },
            returnToBillingProfile() {
                this.$router.push('/app/subscription/billing');
            },
        },
    };
</script>

<style scoped lang="scss"></style>
